import { createSelector, Dispatch } from '@reduxjs/toolkit';
import { mapActivityDetailByDetailResult, mapLogisticsByDetail } from 'gql/service/graph/activityService';
import { selectCmsFromBootstrap, selectConfigFromBootstrap, selectExperimentsFromBootstrap, selectFeatureExperimentsFromBootstrap } from 'store/slice/bootstrap';
import {
    selectActivityDetail,
    selectActivityFromContentDetailState,
    selectActivityItinerary,
    selectActivityLocationFromContentDetailState,
    selectDetailState,
    selectExtraFeeFromContentDetailGenericSectionState,
    selectIntroductionFromContentDetailGenericSectionState,
    selectPurchaseNoticeContent,
    selectResultFromDetailState,
    selectReviewState,
    selectSelectedTabFromDetailState,
    selectSimCardContent,
    selectSuggestedRouteFromGenericSectionState,
} from 'component/page/detail/store/detail.selectors';
import { isDepartureAndReturnAvailable, isGeoLocationAvailable, isItineraryAvailable, mapAdditionalInfo } from 'component/page/detail/detailUtils';
import { navigateToHashWithTabBarOffset } from 'component/common/DetailInViewWrapper/navigateToHashWithTabBarOffset';
import { setExpandedAccordionOnDetailsPage, setIsScrolling, setSelectedTabOnDetailsPage } from 'component/page/detail/store/details';
import { NavigationHashEnum } from 'core/type/Details.types';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import { ActivitiesDetailsPageTabClickEventActivitiesFieldsV1 } from '@agoda/analytics-messages-definition-activities/events.split';
import { ActivitiesDetailsPageTabClickEventActivitiesManifestV1 } from '@agoda/analytics-messages-definition-activities/manifest/events';
import { SCROLLING_TIMEOUT } from 'core/constant/AnimationTimeout';
import { CmsValue } from 'core/type/BootstrapConfig.types';
import { isB, isOn } from 'core/window/bootstrapConfig';
import { ExperimentId } from 'core/type/ExperimentId';
import { Flex, Width } from '@drone-js/core';
import { FeatureName } from 'core/constant/FeatureName';
import { OVERVIEW_ELEMENT_NAME } from 'component/page/detail/DetailCard/Overview/ActivityOverview';

export type DetailPageTabsProps = StateProps & DispatchProps;

interface StateProps {
    isSuccess: boolean;
    tabs: Tab[];
    selectedTab: string;
    cms: CmsValue;
}

export interface DispatchProps {
    onTabChanged: (tab: Tab) => void;
}

export interface Tab {
    title: string;
    id: NavigationHashEnum;
    href: string;
}

type TabSize = 's' | 'm';
export interface TabIndicatorProps {
    size?: TabSize;
}

export interface TabProps extends React.HTMLAttributes<HTMLButtonElement> {
    isActive?: boolean;
    size?: TabSize;
    lineClamp?: number;
    flex?: Flex;
    width?: Width;
    label: string;
}

export const shiftFocusByIndexAndHref = (index: number, href: string) => {
    if (index === 0) {
        const overviewTab = document.querySelector(`#${href}`) as HTMLElement;
        if (overviewTab?.parentElement?.dataset?.elementName === OVERVIEW_ELEMENT_NAME) {
            const mosaicGallery1stItem = document.querySelector('[data-element-name="activities-mosaic-gallery"][data-element-index="1"]') as HTMLElement;
            if (!mosaicGallery1stItem) {
                return;
            }
            mosaicGallery1stItem.focus && mosaicGallery1stItem.focus();
            return;
        }
    }

    const nextFocusElementWithButton = document.querySelector(`#${href} button`) as HTMLElement;
    const nextFocusElementWithButtonOrHeading = nextFocusElementWithButton || (document.querySelector(`#${href}`) as HTMLElement);
    setTimeout(() => {
        nextFocusElementWithButtonOrHeading?.focus();
    }, 500);
};

export const selectOverviewTab = createSelector(selectCmsFromBootstrap, selectActivityFromContentDetailState, selectConfigFromBootstrap, (cms, activity, config) => {
    const isDetailPageRevampEnabled = isOn(FeatureName.DetailPageRevamp, config);
    if (isDetailPageRevampEnabled || !activity || !activity.description || activity.description.length === 0) {
        return null;
    }
    return { title: cms[126848], id: NavigationHashEnum.OverviewSection };
});

export const selectSimCardSpecTab = createSelector(selectCmsFromBootstrap, selectSimCardContent, (cms, simCardContent) => {
    if (simCardContent && simCardContent.length > 0) {
        return { title: cms[244232], id: NavigationHashEnum.SimCardSpecSection };
    }
    return null;
});

export const selectIntroductionSummaryTab = createSelector(selectIntroductionFromContentDetailGenericSectionState, (introductionSummary) => {
    if (introductionSummary?.title) {
        return { title: introductionSummary.title, id: NavigationHashEnum.IntroductionSummarySection };
    }
    return null;
});

export const selectPackageOptionTab = createSelector(selectCmsFromBootstrap, (cms) => {
    return { title: cms[238726], id: NavigationHashEnum.PackageOptionsSection };
});

export const selectWhatIncludedTab = createSelector(selectCmsFromBootstrap, selectActivityDetail, selectExtraFeeFromContentDetailGenericSectionState, (cms, detail, extraFee) => {
    const inclusionsCount = detail?.inclusions?.[0]?.benefitItems?.length ?? 0;
    const exclusionsCount = detail?.exclusions?.[0]?.benefitItems?.length ?? 0;
    const extraFeeCount = extraFee ? 1 : 0;
    if (inclusionsCount + exclusionsCount + extraFeeCount === 0) {
        return null;
    }
    return { title: cms[126850], id: NavigationHashEnum.WhatsIncludedSection };
});

export const selectDepartureAndReturnTab = createSelector(selectCmsFromBootstrap, selectResultFromDetailState, selectExperimentsFromBootstrap, (cms, detailResult, experiments) => {
    const detail = mapActivityDetailByDetailResult(detailResult);
    const logistics = mapLogisticsByDetail(detail);
    const departureAndReturn = isDepartureAndReturnAvailable(logistics, isB(ExperimentId.ABE_4553, experiments));
    if (departureAndReturn) {
        return { title: cms[126851], id: NavigationHashEnum.DepartureReturnSection };
    }
    return null;
});
export const selectWhatToExpectTab = createSelector(selectCmsFromBootstrap, selectActivityItinerary, (cms, itinerary) => {
    if (isItineraryAvailable(itinerary)) {
        return { title: cms[126852], id: NavigationHashEnum.WhatToExpectSection };
    }
    return null;
});

export const selectLocationTab = createSelector(
    selectActivityLocationFromContentDetailState,
    selectCmsFromBootstrap,
    selectExperimentsFromBootstrap,
    (ActivityLocation, cms, experiments) => {
        if (isB(ExperimentId.AFT_218, experiments) && isGeoLocationAvailable(ActivityLocation)) {
            return { title: cms[20536], id: NavigationHashEnum.LocationSection };
        }
        return null;
    },
);

export const selectSuggestedRouteTab = createSelector(selectCmsFromBootstrap, selectSuggestedRouteFromGenericSectionState, (cms, suggestedRoute) => {
    if (suggestedRoute) {
        return { title: cms[244231], id: NavigationHashEnum.SuggestedRouteSection };
    }
    return null;
});

export const selectAdditionalInfoTab = createSelector(selectCmsFromBootstrap, selectResultFromDetailState, selectPurchaseNoticeContent, (cms, detailResult, purchaseNotice) => {
    const additionalInformation = mapAdditionalInfo(detailResult) || [];
    const additionalInfo = additionalInformation.length + purchaseNotice.length > 0;
    if (additionalInfo) {
        return { title: cms[130768], id: NavigationHashEnum.AdditionalInfoSection };
    }
    return null;
});

export const selectCancellationPolicyTab = createSelector(selectCmsFromBootstrap, (cms) => ({ title: cms[126854], id: NavigationHashEnum.CancellationPolicySection }));

export const selectReviewTab = createSelector(selectCmsFromBootstrap, selectReviewState, (cms, reviewState) => {
    if (reviewState.isHideReview) {
        return null;
    }
    return { title: cms[102057], id: NavigationHashEnum.ReviewSection };
});

export const selectFAQsTab = createSelector(selectCmsFromBootstrap, (cms) => {
    return { title: cms[276903], id: NavigationHashEnum.FAQsSection };
});

export const selectAboutActivityTab = createSelector(selectCmsFromBootstrap, selectActivityDetail, selectFeatureExperimentsFromBootstrap, (cms, detail, featureExperiments) => {
    if (!detail?.aboutActivity || !featureExperiments.featureBehavior.ShowAboutActivityPanel) {
        return null;
    }
    return { title: cms[337805], id: NavigationHashEnum.AboutActivitySection };
});

export const TabId = 'activity-nav-tabs';

export const selectTabs = createSelector(
    selectOverviewTab,
    selectSimCardSpecTab,
    selectIntroductionSummaryTab,
    selectPackageOptionTab,
    selectDepartureAndReturnTab,
    selectWhatToExpectTab,
    selectLocationTab,
    selectSuggestedRouteTab,
    selectAdditionalInfoTab,
    selectCancellationPolicyTab,
    selectReviewTab,
    selectFAQsTab,
    selectAboutActivityTab,
    (
        activityOverviewTab,
        simCardSpecTab,
        introductionSummaryTab,
        packageOptionTab,
        departureAndReturnTab,
        whatToExpectTab,
        locationTab,
        suggestedRouteTab,
        additionalInfoTab,
        cancellationPolicyTab,
        reviewTab,
        faqsTab,
        aboutActivityTab,
    ) => {
        // Put tabs in the display order
        const tabs = [
            activityOverviewTab,
            packageOptionTab,
            simCardSpecTab,
            introductionSummaryTab,
            departureAndReturnTab,
            whatToExpectTab,
            locationTab,
            suggestedRouteTab,
            additionalInfoTab,
            cancellationPolicyTab,
            faqsTab,
            aboutActivityTab,
            reviewTab,
        ]
            .filter((tab): tab is Tab => !!tab)
            .map((tab, idx) => {
                return { ...tab, href: `${TabId}-panel-${idx + 1}` };
            });
        return { tabs };
    },
);

export const mapStateToProps = createSelector(
    selectDetailState,
    selectTabs,
    selectSelectedTabFromDetailState,
    selectCmsFromBootstrap,
    (state, tabsObject, selectedTab, cms): StateProps => {
        if (!state.isSuccess) {
            return { tabs: [], isSuccess: false, selectedTab, cms };
        }
        return { ...tabsObject, isSuccess: true, selectedTab, cms };
    },
);

export const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
    onTabChanged: (tab: Tab) => {
        dispatch(setExpandedAccordionOnDetailsPage({ selector: `#${tab.href}`, status: true }));
        navigateToHashWithTabBarOffset(dispatch, `#${tab.href}`, setIsScrolling);
        trackElementEvent<ActivitiesDetailsPageTabClickEventActivitiesFieldsV1>(ActivitiesDetailsPageTabClickEventActivitiesManifestV1.ids, {}, { action_element_value: tab.href });
        // prevent tabs focus
        dispatch(setIsScrolling(true));
        setTimeout(() => {
            dispatch(setIsScrolling(false));
            setTimeout(() => {
                dispatch(setSelectedTabOnDetailsPage(tab.href)); // Need to dispatch slightly after reactivated InView to override selectedTab
            }, 40);
        }, SCROLLING_TIMEOUT);
    },
});
